import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { useProductDetail } from '@/contexts/ProductDetailContext';
import { useCartStore } from '@/store/cartStore';
import { useFavoritesStore } from '@/store/favoritesStore';
import { useUserStore } from '@/store/userStore';
import { Product } from '@/types/product';
import {
  AlertCircleIcon,
  HealthIcon,
  ViewIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import React, { useState } from 'react';
import placeholder from '../assets/medication-placeholder.webp';
import { PhoneNumberModal } from './PhoneNumberModal';
import { toast } from './ToastProvider';

interface ProductCardProps extends Product {
  tags?: string[];
}

export function ProductCard({
  id,
  brand_name,
  price,
  image_url,
  category,
  tags = [],
  strength,
  sales,
  quantity,
  active_ingredient,
  type,
  dosage_form,
  pack_size,
  image_urls,
  class: productClass,
}: ProductCardProps) {
  const { addItem, getItem, updateQuantity } = useCartStore();
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();
  const { openProductDetail } = useProductDetail();
  const favorite = id ? isFavorite(id) : false;
  const [showTooltip, setShowTooltip] = React.useState(false);
  const phoneNumber = useUserStore(s => s.phoneNumber);
  const setPhoneNumber = useUserStore(s => s.setPhoneNumber);

  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [pendingFavorite, setPendingFavorite] = useState(false);

  const isPOM = category?.toUpperCase() === 'POM';
  const displayImage = image_urls?.[0] || image_url || placeholder;
  const productId = id || brand_name.toLowerCase().replace(/\s+/g, '-');
  const availableQuantity = quantity ?? 0;

  const cartItem = getItem(productId);
  const quantityInCart = cartItem?.quantity ?? 0;

  const handleAddToCart = () => {
    if (isPOM) {
      toast.info(
        "This medication requires a prescription. You'll need to provide one during checkout."
      );
    }

    addItem({
      id: productId,
      brand_name,
      price,
      image: image_urls?.[0] || image_url || null,
      category: category || null,
      availableQuantity,
    });

    toast.success('Product added to cart');
  };

  const handleIncrease = () => {
    if (quantityInCart < availableQuantity) {
      updateQuantity(productId, quantityInCart + 1);
    }
  };

  const handleDecrease = () => {
    updateQuantity(productId, quantityInCart - 1);
  };

  const toggleFavorite = (providedPhone?: string) => {
    const phoneToUse = providedPhone || phoneNumber;

    if (!phoneToUse) {
      setPendingFavorite(true);
      setShowPhoneModal(true);
      return;
    }

      // Use props to construct the product object
      // We assume ProductCardProps contains all necessary Product fields since it extends Product
      const { tags, ...productData } = this.props; // Wait, can't access this.props in functional component.
      // We need to use values from scope.
      // But we destructured props at top level.
      // Best way: Create object from available variables which are destructured props.

      const productForFavorites: Product = {
        id: productId,
        brand_name,
        price,
        image_url: displayImage,
        category,
        sales,
        quantity,
        active_ingredient,
        type,
        dosage_form,
        pack_size,
        class: productClass,
        image_urls,
        strength,
        threshold: 0,
        created_at: '',
        updated_at: '',
        branch_id: '',
      };
    // BETTER APPROACH: Just use the props as the product object, casting it.

    // Let's assume we have all necessary props because we spread {...product}.

    if (favorite) {
      removeFavorite(productId);
      toast.success(`${brand_name} has been removed from your favorites`);
    } else {
      // @ts-ignore - Constructing favorited item
      addFavorite({ id, brand_name, price, image_url, category, quantity, sales, active_ingredient, type, dosage_form, pack_size, class: productClass, image_urls, strength } as Product);
      toast.success(`${brand_name} has been added to your favorites`);
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite();
  };

  const handlePhoneSubmit = (phone: string) => {
    setPhoneNumber(phone);
    setShowPhoneModal(false);
    if (pendingFavorite) {
      setPendingFavorite(false);
      toggleFavorite(phone);
    }
  };

  const handleLearnMore = (e: React.MouseEvent) => {
    e.preventDefault();
    openProductDetail({
      id: productId,
      brand_name,
      price,
      image_url: displayImage,
      category,
      // @ts-ignore
      prescription: isPOM,
      frequentlyBought: (sales || 0) > 5,
      strength,
      sales,
      quantity,
      active_ingredient,
      type,
      dosage_form,
      pack_size,
      class: productClass,
    } as any); // Context might need update too, but using any for now to unblock
  };

  return (
    <>
      <Card className="h-full rounded-lg group overflow-hidden hover:shadow-sm transition-shadow duration-200 ease-out-cubic flex flex-col">
        <CardHeader className="p-0 relative">
          <div className="relative w-full bg-[var(--dispensary-gray-50)] aspect-square">
            <img
              src={displayImage}
              alt={brand_name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-200 ease-out-cubic group-hover: scale-105"
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== placeholder) {
                  target.src = placeholder;
                }
              }}
            />
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-1. 5 right-1.5 rounded-full bg-background/80 hover:bg-background h-6 w-6"
            onClick={handleToggleFavorite}
          >
            <HugeiconsIcon
              icon={HealthIcon}
              size={12}
              className={`${
                favorite
                  ? 'fill-[var(--famasi-red-500)] text-[var(--famasi-red-500)]'
                  : ''
              }`}
            />
          </Button>

          {isPOM && (
            <div
              className="absolute top-1.5 left-1.5 bg-[var(--famasi-xanthous-yellow-300)] p-0.5 rounded-full"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
              tabIndex={0}
              style={{ outline: 'none' }}
            >
              <HugeiconsIcon
                icon={AlertCircleIcon}
                size={10}
                className="text-[var(--famasi-xanthous-yellow-900)]"
              />
              <AnimatePresence>
                {showTooltip && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.2,
                        ease: [0.215, 0.61, 0.355, 1],
                      },
                    }}
                    exit={{
                      opacity: 0,
                      y: 8,
                      transition: {
                        duration: 0.2,
                        ease: [0.215, 0.61, 0.355, 1],
                      },
                    }}
                    className="absolute left-1/2 -translate-x-1/2 mt-1. 5 z-50 px-2 py-1 rounded-md bg-white text-[var(--dispensary-gray-800)] text-[10px] shadow-sm border whitespace-nowrap"
                    style={{ top: '100%' }}
                  >
                    Prescription required
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </CardHeader>

        <CardContent className="flex-grow p-2 sm:p-2.5">
          <div className="mb-0.5">
            {tags.map(tag => (
              <Badge
                key={tag}
                variant="secondary"
                className="mr-0.5 mb-0.5 text-[8px] sm:text-[9px] px-1 py-0"
              >
                {tag}
              </Badge>
            ))}
          </div>
          <h3 className="text-primary font-medium line-clamp-2 text-xs sm:text-sm mb-0.5 leading-tight">
            {brand_name}
          </h3>
          <div className="flex items-center justify-between">
            {price && (
              <span className="font-semibold text-xs sm:text-sm">
                ₦{price.toLocaleString()}
              </span>
            )}
          </div>
        </CardContent>

        <CardFooter className="p-2 pt-0">
          <div className="grid grid-cols-2 gap-1. 5 w-full">
            <Button
              className="rounded-full text-[10px] h-7 px-2 bg-[var(--dispensary-gray-100)] text-[var(--dispensary-gray-900)] hover:bg-[var(--dispensary-gray-200)]"
              variant="secondary"
              onClick={handleLearnMore}
            >
              <HugeiconsIcon icon={ViewIcon} size={10} className="mr-0.5" />
              Details
            </Button>
            {availableQuantity === 0 ? (
              <Button className="rounded-full text-[10px] h-7 px-2" disabled>
                Out of Stock
              </Button>
            ) : quantityInCart > 0 ? (
              <div className="flex items-center justify-between rounded-full bg-primary h-7 px-1">
                <button
                  type="button"
                  onClick={handleDecrease}
                  className="flex items-center justify-center rounded-full text-primary-foreground w-5 h-5"
                >
                  <Minus size={10} />
                </button>
                <span className="text-[10px] text-primary-foreground">
                  {quantityInCart}
                </span>
                <button
                  type="button"
                  onClick={handleIncrease}
                  disabled={quantityInCart >= availableQuantity}
                  className="flex items-center justify-center rounded-full text-primary-foreground disabled:opacity-50 w-5 h-5"
                >
                  <Plus size={10} />
                </button>
              </div>
            ) : (
              <Button
                className="rounded-full text-[10px] h-7 px-2"
                onClick={handleAddToCart}
              >
                Add to Cart
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>

      <PhoneNumberModal
        open={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSubmit={handlePhoneSubmit}
      />
    </>
  );
}
